import type { FixEntrySource } from '@/lib/ai/coding-agent-service/fix-tracking';

import { asRecord } from '@/lib/pr-v2/utils/as-record';

export type PrFixRequestResult =
  | { kind: 'started' }
  | { kind: 'already-running'; runUrl: string }
  | { kind: 'failed'; message: string };

const FAILED_MESSAGE = 'Failed to generate fix';

export async function requestPrFix({
  owner,
  repo,
  number,
  reviewSessionId,
  violationIds,
  entrySource,
}: {
  owner: string;
  repo: string;
  number: string;
  reviewSessionId: string;
  violationIds: readonly string[];
  entrySource: FixEntrySource;
}): Promise<PrFixRequestResult> {
  try {
    const response = await fetch(
      `/api/fix/pr/${encodeURIComponent(owner)}/${encodeURIComponent(repo)}/${encodeURIComponent(number)}/${encodeURIComponent(reviewSessionId)}`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ violationIds, entrySource }),
      },
    );
    if (!response.ok) {
      const error = asRecord(await response.json().catch(() => null));
      // A fix already running on the pull request answers with its fix session.
      const runUrl = error?.runUrl;
      if (response.status === 409 && typeof runUrl === 'string' && runUrl) {
        return { kind: 'already-running', runUrl };
      }

      return {
        kind: 'failed',
        message: typeof error?.error === 'string' ? error.error : FAILED_MESSAGE,
      };
    }

    return { kind: 'started' };
  } catch (error) {
    return { kind: 'failed', message: error instanceof Error ? error.message : FAILED_MESSAGE };
  }
}
